import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X, Trash2, Image as ImageIcon, Loader2, Heart, Filter, Wallet, TrendingUp, FolderPlus } from 'lucide-react';
import { AddToProjectPicker } from './ProjectsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'furniture', label: 'Möbel', emoji: '🛋️' },
  { id: 'decor',     label: 'Dekor', emoji: '🌸' },
  { id: 'color',     label: 'Färg',  emoji: '🎨' },
  { id: 'layout',    label: 'Layout',emoji: '📐' },
  { id: 'lighting',  label: 'Ljus',  emoji: '💡' },
  { id: 'textile',   label: 'Textil',emoji: '🧶' },
  { id: 'other',     label: 'Annat', emoji: '✨' },
];

const STATUSES = [
  { id: 'want',   label: 'Vill ha',   emoji: '💗', color: '#FFE8F5' },
  { id: 'maybe',  label: 'Kanske',    emoji: '🤔', color: '#E4D6F5' },
  { id: 'got',    label: 'Har den',   emoji: '✅', color: '#B9FBC0' },
  { id: 'passed', label: 'Avstått',   emoji: '🍃', color: '#F0F0F0' },
];

function compressImage(file, cb) {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const maxW = 1280;
      let w = img.width;
      let h = img.height;
      if (w > maxW) { h = Math.round(h * (maxW / w)); w = maxW; }
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      c.getContext('2d').drawImage(img, 0, 0, w, h);
      let url = c.toDataURL('image/jpeg', 0.72);
      if (url.length > 2_400_000) url = c.toDataURL('image/jpeg', 0.55);
      cb(url);
    };
    img.onerror = () => cb(null);
    img.src = reader.result;
  };
  reader.onerror = () => cb(null);
  reader.readAsDataURL(file);
}

function AddPinModal({ open, onClose, onCreated, rooms, defaultRoomId }) {
  const [image, setImage] = useState(null);
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [roomId, setRoomId] = useState(defaultRoomId || '');
  const [category, setCategory] = useState('furniture');
  const [status, setStatus] = useState('want');
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setImage(null); setTitle(''); setNote('');
      setRoomId(defaultRoomId || '');
      setCategory('furniture'); setStatus('want'); setPrice('');
    }
  }, [open, defaultRoomId]);

  if (!open) return null;

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f || !f.type.startsWith('image/')) return;
    setBusy(true);
    compressImage(f, (url) => { setImage(url); setBusy(false); });
  };

  const submit = async () => {
    if (!image) { toast.error('Välj en bild först'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/inspiration/pins`, {
        image, title: title.trim() || null, note: note.trim() || null,
        room_id: roomId || null, category, status,
      }, { withCredentials: true });
      toast.success('Sparat på tavlan ✨');
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte spara');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="pin-modal">
      <div onClick={(e) => e.stopPropagation()} className="neo-card bg-white p-4 w-full max-w-md space-y-3 max-h-[96vh] overflow-y-auto">
        <div className="flex justify-between items-center">
          <h3 className="font-['Fredoka'] font-bold text-lg">Ny inspiration 📌</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        {!image ? (
          <label className="neo-btn flex flex-col items-center justify-center gap-2 py-8 bg-[#FFE8F5] text-sm font-bold cursor-pointer border-dashed border-2 border-[#D06892]/40" data-testid="pin-photo-add">
            {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImageIcon className="w-6 h-6" />}
            <span>{busy ? 'Bearbetar...' : 'Välj bild'}</span>
            <input type="file" accept="image/*" onChange={handleFile} className="hidden" disabled={busy} data-testid="pin-photo-input" />
          </label>
        ) : (
          <div className="relative neo-card p-1 bg-white">
            <img src={image} alt="preview" className="max-h-48 w-full object-cover rounded" />
            <button type="button" onClick={() => setImage(null)} className="absolute -top-2 -right-2 bg-white rounded-full shadow border-2 border-red-300 p-1">
              <X className="w-3 h-3 text-red-500" />
            </button>
          </div>
        )}

        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Titel (valfri)"
          className="neo-input w-full text-sm"
          data-testid="pin-title"
        />

        <div>
          <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Rum (valfritt)</p>
          <select value={roomId} onChange={(e) => setRoomId(e.target.value)} className="neo-input w-full text-sm" data-testid="pin-room">
            <option value="">— Inget rum —</option>
            {rooms.map((r) => (
              <option key={r.id} value={r.id}>{r.emoji || '🏠'} {r.name}</option>
            ))}
          </select>
        </div>

        <div>
          <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Kategori</p>
          <div className="grid grid-cols-4 gap-1">
            {CATEGORIES.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => setCategory(c.id)}
                className={`p-1.5 rounded border-2 text-[10px] font-bold ${category === c.id ? 'bg-[#FFE8F5] border-[#D06892]' : 'bg-white border-black/10'}`}
                data-testid={`pin-cat-${c.id}`}
              >
                <span className="text-base block">{c.emoji}</span>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Status</p>
          <div className="grid grid-cols-4 gap-1">
            {STATUSES.map((s) => (
              <button
                type="button"
                key={s.id}
                onClick={() => setStatus(s.id)}
                className={`p-1.5 rounded border-2 text-[10px] font-bold ${status === s.id ? 'ring-2 ring-[#D06892]' : ''}`}
                style={{ backgroundColor: status === s.id ? s.color : 'white', borderColor: status === s.id ? '#D06892' : 'rgba(0,0,0,0.1)' }}
                data-testid={`pin-status-${s.id}`}
              >
                <span className="text-base block">{s.emoji}</span>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Anteckning..."
          rows={2}
          className="neo-input w-full text-sm"
          data-testid="pin-note"
        />

        <div>
          <p className="text-[10px] font-bold uppercase opacity-60 mb-1 flex items-center gap-1">
            <Wallet className="w-3 h-3" /> Pris (kr, valfritt)
          </p>
          <input
            type="number" inputMode="decimal" min="0" step="1"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="t.ex. 1299"
            className="neo-input w-full text-sm"
            data-testid="pin-price"
          />
        </div>

        <button onClick={submit} disabled={busy || !image} className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold text-base sticky bottom-0" data-testid="pin-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Spara på tavlan ✨'}
        </button>
      </div>
    </div>
  );
}

function PinCard({ pin, onDelete, onClick }) {
  const cat = CATEGORIES.find((c) => c.id === pin.category) || CATEGORIES[0];
  const st = STATUSES.find((s) => s.id === pin.status) || STATUSES[0];
  return (
    <div
      className="neo-card bg-white overflow-hidden break-inside-avoid mb-2 cursor-pointer hover:shadow-lg transition-shadow"
      onClick={onClick}
      data-testid={`pin-${pin.id}`}
    >
      <img src={pin.image} alt={pin.title || 'pin'} className="w-full" />
      <div className="p-2">
        <div className="flex items-center gap-1 flex-wrap mb-1">
          <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: st.color }}>
            {st.emoji} {st.label}
          </span>
          <span className="text-[9px] opacity-60">{cat.emoji} {cat.label}</span>
          {pin.room && <span className="text-[9px] opacity-60">{pin.room.emoji} {pin.room.name}</span>}
        </div>
        {pin.title && <p className="text-xs font-bold truncate">{pin.title}</p>}
        {pin.price != null && pin.price > 0 && (
          <p className="text-[11px] font-bold text-[#D06892] mt-0.5" data-testid={`pin-price-${pin.id}`}>
            {Math.round(pin.price).toLocaleString('sv-SE')} kr
          </p>
        )}
        {pin.note && <p className="text-[10px] opacity-60 line-clamp-2">{pin.note}</p>}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(pin.id); }}
          className="text-[9px] opacity-40 hover:opacity-100 mt-1"
          data-testid={`pin-delete-${pin.id}`}
        >
          <Trash2 className="w-3 h-3 inline" /> Ta bort
        </button>
      </div>
    </div>
  );
}

function CostSummary({ summary, busy }) {
  if (busy) {
    return (
      <div className="neo-card p-6 text-center bg-white" data-testid="cost-summary-loading">
        <Loader2 className="w-5 h-5 animate-spin mx-auto opacity-60" />
      </div>
    );
  }
  if (!summary) return null;

  const budget = Number(summary.monthly_budget) || 0;
  const total = summary.total || {};
  const want = Number(total.want) || 0;
  const all = Number(total.all) || 0;
  const ratio = budget > 0 ? Math.min(want / budget, 2) : 0;
  const overBudget = budget > 0 && want > budget;

  const fmt = (n) => (Number(n) || 0).toLocaleString('sv-SE', { maximumFractionDigits: 0 });

  const rooms = (summary.rooms || []).filter((r) => (r.all || 0) > 0 || (r.pin_count || 0) > 0);

  return (
    <div className="space-y-3" data-testid="cost-summary">
      {/* Top: total + budget */}
      <section className="neo-card p-4 bg-gradient-to-br from-[#FFF0F8] via-[#F0F5FF] to-[#FFFAF0]">
        <div className="flex items-center gap-2 mb-2">
          <Wallet className="w-5 h-5 text-[#D06892]" />
          <h3 className="font-['Fredoka'] font-bold text-lg">Kostnadsöversikt</h3>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="neo-card p-2 bg-[#FFE8F5]">
            <p className="text-[10px] uppercase opacity-60 font-bold">💗 Vill ha</p>
            <p className="text-base font-bold font-['Fredoka']" data-testid="cost-total-want">{fmt(want)} kr</p>
          </div>
          <div className="neo-card p-2 bg-[#B9FBC0]">
            <p className="text-[10px] uppercase opacity-60 font-bold">✅ Har</p>
            <p className="text-base font-bold font-['Fredoka']" data-testid="cost-total-got">{fmt(total.got)} kr</p>
          </div>
          <div className="neo-card p-2 bg-white">
            <p className="text-[10px] uppercase opacity-60 font-bold">📊 Totalt</p>
            <p className="text-base font-bold font-['Fredoka']" data-testid="cost-total-all">{fmt(all)} kr</p>
          </div>
        </div>

        {/* Budget bar */}
        {budget > 0 ? (
          <div className="mt-3" data-testid="cost-budget-bar">
            <div className="flex justify-between text-[11px] font-bold mb-1">
              <span>💗 Önskelista vs månadsbudget</span>
              <span className={overBudget ? 'text-red-600' : 'text-[#3A7A4A]'}>
                {fmt(want)} / {fmt(budget)} kr
              </span>
            </div>
            <div className="w-full h-3 rounded-full border-2 border-black overflow-hidden bg-white">
              <div
                className="h-full transition-all"
                style={{
                  width: `${Math.min(ratio * 100, 200)}%`,
                  background: overBudget
                    ? 'linear-gradient(90deg,#FFB4A2,#FF6B6B)'
                    : 'linear-gradient(90deg,#B9FBC0,#A2D2FF)',
                }}
              />
            </div>
            <p className="text-[10px] opacity-70 mt-1" data-testid="cost-budget-msg">
              {overBudget
                ? `⚠️ ${fmt(want - budget)} kr över din månadsbudget — fundera på att flytta något till "Kanske"`
                : budget > 0
                ? `🌸 ${fmt(budget - want)} kr kvar i månadsbudgeten`
                : ''}
            </p>
          </div>
        ) : (
          <p className="text-[11px] opacity-60 mt-3" data-testid="cost-no-budget">
            💡 Sätt en månadsbudget under <strong>Ekonomi → Att köpa & Budget</strong> för att jämföra dina drömmar med din plånbok.
          </p>
        )}
      </section>

      {/* Per-room breakdown */}
      {rooms.length === 0 ? (
        <p className="text-center text-xs opacity-60 italic py-6" data-testid="cost-empty">
          Inga priser tillagda än 💸 — lägg till pris på dina pins för att se totaler per rum.
        </p>
      ) : (
        <section className="space-y-2" data-testid="cost-rooms">
          {rooms.map((r) => {
            const want = Number(r.want) || 0;
            const got = Number(r.got) || 0;
            const maybe = Number(r.maybe) || 0;
            const all = Number(r.all) || 0;
            const roomKey = r.room_id || 'unassigned';
            return (
              <div key={roomKey} className="neo-card p-3 bg-white" data-testid={`cost-room-${roomKey}`}>
                <div className="flex items-center gap-2">
                  {r.photo ? (
                    <img src={r.photo} alt={r.name} className="w-12 h-12 rounded-lg object-cover border-2 border-[#D06892]/40" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl bg-[#FFE8F5] border-2 border-[#D06892]/40">
                      {r.emoji || '🏠'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold font-['Fredoka'] text-sm truncate">{r.emoji || '🏠'} {r.name || 'Osorterat'}</p>
                    <p className="text-[10px] opacity-60">
                      📌 {r.pin_count} pins · {r.priced_count} med pris
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] uppercase opacity-60 font-bold">Totalt</p>
                    <p className="text-base font-bold font-['Fredoka'] text-[#D06892]">{fmt(all)} kr</p>
                  </div>
                </div>

                {/* Status breakdown */}
                <div className="grid grid-cols-3 gap-1 mt-2 text-[10px]">
                  <div className="bg-[#FFE8F5] rounded-md px-2 py-1 text-center font-bold">
                    💗 Vill ha<br /><span className="font-['Fredoka']">{fmt(want)} kr</span>
                  </div>
                  <div className="bg-[#E4D6F5] rounded-md px-2 py-1 text-center font-bold">
                    🤔 Kanske<br /><span className="font-['Fredoka']">{fmt(maybe)} kr</span>
                  </div>
                  <div className="bg-[#B9FBC0] rounded-md px-2 py-1 text-center font-bold">
                    ✅ Har<br /><span className="font-['Fredoka']">{fmt(got)} kr</span>
                  </div>
                </div>

                {/* Per-room budget hint */}
                {budget > 0 && want > 0 && (
                  <p className="text-[10px] opacity-70 mt-2 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    Drömmen i detta rum = <strong>{Math.round((want / budget) * 100)}%</strong> av månadsbudgeten
                  </p>
                )}
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}

function WishlistView({ pins, onDelete, onAddToProject }) {
  // Only "want" status, sorted by price (desc; null prices last)
  const wishlist = pins
    .filter((p) => p.status === 'want')
    .slice()
    .sort((a, b) => {
      const pa = a.price ?? -1;
      const pb = b.price ?? -1;
      return pb - pa;
    });

  const total = wishlist.reduce((sum, p) => sum + (Number(p.price) || 0), 0);
  const pricedCount = wishlist.filter((p) => p.price != null && p.price > 0).length;

  if (wishlist.length === 0) {
    return (
      <div className="text-center py-12 opacity-70" data-testid="wishlist-empty">
        <Heart className="w-10 h-10 mx-auto mb-2 text-[#D06892]" />
        <p className="text-sm font-bold">Önskelistan är tom 💗</p>
        <p className="text-xs opacity-60">Pins med status "Vill ha" hamnar här</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="wishlist-view">
      <section className="neo-card p-4 bg-gradient-to-br from-[#FFE8F5] via-[#FFF0F8] to-[#FFFAF0]">
        <div className="flex items-center gap-2 mb-2">
          <Heart className="w-5 h-5 text-[#D06892] fill-[#D06892]" />
          <h3 className="font-['Fredoka'] font-bold text-lg">Min "Vill ha"-logg</h3>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div className="neo-card p-2 bg-white">
            <p className="text-[10px] uppercase opacity-60 font-bold">💗 Pins</p>
            <p className="text-base font-bold font-['Fredoka']" data-testid="wishlist-count">{wishlist.length}</p>
          </div>
          <div className="neo-card p-2 bg-white">
            <p className="text-[10px] uppercase opacity-60 font-bold">Med pris</p>
            <p className="text-base font-bold font-['Fredoka']">{pricedCount}</p>
          </div>
          <div className="neo-card p-2 bg-[#FFC8DD]">
            <p className="text-[10px] uppercase opacity-60 font-bold">Total</p>
            <p className="text-base font-bold font-['Fredoka'] text-[#D06892]" data-testid="wishlist-total">
              {Math.round(total).toLocaleString('sv-SE')} kr
            </p>
          </div>
        </div>
      </section>

      <div className="columns-2 sm:columns-3 lg:columns-4 gap-2" data-testid="wishlist-grid">
        {wishlist.map((p) => (
          <PinCard key={p.id} pin={p} onDelete={onDelete} onAddToProject={onAddToProject} onClick={() => {}} />
        ))}
      </div>
    </div>
  );
}

function RoomMoodboard({ room, pins }) {
  if (!room.room_id || pins.length === 0) return null;
  return (
    <section className="neo-card p-3 bg-white mb-3" data-testid={`moodboard-${room.room_id}`}>
      <div className="flex items-center gap-3 mb-2">
        {room.photo && <img src={room.photo} alt={room.name} className="w-16 h-16 rounded-lg object-cover border-2 border-[#D06892]/40" />}
        <div className="flex-1">
          <p className="text-[10px] font-bold uppercase opacity-60">Drömrum</p>
          <h3 className="font-['Fredoka'] font-bold text-base">{room.emoji} {room.name}</h3>
          <p className="text-[10px] opacity-60">💗 {room.want} vill ha · ✅ {room.got} har</p>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
        {pins.slice(0, 8).map((p) => {
          const st = STATUSES.find((s) => s.id === p.status) || STATUSES[0];
          return (
            <div key={p.id} className="relative" data-testid={`moodboard-pin-${p.id}`}>
              <img src={p.image} alt={p.title || 'pin'} className="w-full aspect-square object-cover rounded border-2 border-white shadow" />
              <span
                className="absolute top-0.5 right-0.5 text-[9px] px-1 rounded-full"
                style={{ backgroundColor: st.color }}
              >
                {st.emoji}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function InspirationTab() {
  const [pins, setPins] = useState([]);
  const [rooms, setRooms] = useState([]); // konmari rooms (for select)
  const [summary, setSummary] = useState([]); // per-room counts + photos
  const [costSummary, setCostSummary] = useState(null);
  const [costLoading, setCostLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [pickerPinId, setPickerPinId] = useState(null);
  const [filterRoom, setFilterRoom] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [view, setView] = useState('grid'); // 'grid' | 'rooms' | 'cost' | 'wishlist'

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filterRoom) params.room_id = filterRoom;
      if (filterStatus) params.status = filterStatus;
      const [pinsRes, roomsRes, sumRes] = await Promise.all([
        axios.get(`${API}/inspiration/pins`, { params, withCredentials: true }),
        axios.get(`${API}/konmari/tree`, { withCredentials: true }),
        axios.get(`${API}/inspiration/rooms-summary`, { withCredentials: true }),
      ]);
      setPins(pinsRes.data?.pins || []);
      setRooms((roomsRes.data?.nodes || []).filter((n) => n.level === 'room'));
      setSummary(sumRes.data?.rooms || []);
    } catch { /* silent */ }
  }, [filterRoom, filterStatus]);

  const loadCost = useCallback(async () => {
    setCostLoading(true);
    try {
      const { data } = await axios.get(`${API}/inspiration/cost-summary`, { withCredentials: true });
      setCostSummary(data);
    } catch { /* silent */ }
    finally { setCostLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (view === 'cost') loadCost(); }, [view, loadCost, pins]);

  const remove = async (id) => {
    if (!window.confirm('Ta bort denna pin?')) return;
    try {
      await axios.delete(`${API}/inspiration/pins/${id}`, { withCredentials: true });
      load();
    } catch { toast.error('Kunde inte ta bort'); }
  };

  return (
    <div className="space-y-3" data-testid="inspiration-tab">
      <header className="neo-card p-4 bg-gradient-to-br from-[#FFE8F5] via-[#F5EEFF] to-[#E0F4FF]">
        <p className="text-[10px] font-bold tracking-[0.25em] uppercase opacity-60">Inspirationstavla</p>
        <h1 className="font-['Fredoka'] font-bold text-2xl mt-0.5">Drömrum 📌</h1>
        <p className="text-xs opacity-70 mt-1">Spara bilder på möbler & dekor du vill ha — koppla till dina rum för att se hur drömmen tar form.</p>
        <div className="flex gap-2 mt-3">
          <button onClick={() => setShowAdd(true)} className="neo-btn px-3 py-2 bg-[#FFC8DD] text-sm font-bold flex items-center gap-1" data-testid="pin-add-btn">
            <Plus className="w-4 h-4" /> Ny pin
          </button>
          <button
            onClick={() => setView(view === 'grid' ? 'wishlist' : view === 'wishlist' ? 'rooms' : view === 'rooms' ? 'cost' : 'grid')}
            className="neo-btn px-3 py-2 bg-white text-sm font-bold"
            data-testid="view-toggle"
          >
            {view === 'grid' ? '💗 Önskelista' : view === 'wishlist' ? '🏠 Per rum' : view === 'rooms' ? '💰 Kostnad' : '📌 Alla pins'}
          </button>
        </div>
      </header>

      {/* Filters */}
      <section className="neo-card p-2 bg-white/70 flex items-center gap-2 flex-wrap text-[10px] font-bold" data-testid="pin-filters">
        <Filter className="w-3 h-3" />
        <select value={filterRoom} onChange={(e) => setFilterRoom(e.target.value)} className="neo-input text-xs py-1" data-testid="filter-room">
          <option value="">Alla rum</option>
          {rooms.map((r) => <option key={r.id} value={r.id}>{r.emoji || '🏠'} {r.name}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="neo-input text-xs py-1" data-testid="filter-status">
          <option value="">Alla statusar</option>
          {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.emoji} {s.label}</option>)}
        </select>
        {(filterRoom || filterStatus) && (
          <button onClick={() => { setFilterRoom(''); setFilterStatus(''); }} className="text-xs underline opacity-60">Rensa</button>
        )}
      </section>

      {view === 'cost' ? (
        <CostSummary summary={costSummary} busy={costLoading} />
      ) : view === 'wishlist' ? (
        <WishlistView pins={pins} onDelete={remove} onAddToProject={setPickerPinId} />
      ) : view === 'rooms' ? (
        <>
          {summary.filter((r) => r.count > 0).map((r) => (
            <RoomMoodboard key={r.room_id || 'unassigned'} room={r} pins={pins.filter((p) => (p.room_id || null) === r.room_id)} />
          ))}
          {summary.every((r) => r.count === 0) && (
            <p className="text-center text-xs opacity-60 italic py-12">Inga pins än 📌 — börja spara bilder!</p>
          )}
        </>
      ) : (
        <>
          {pins.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-base font-bold mb-1">📌 Tavlan är tom</p>
              <p className="text-xs opacity-60 mb-3">Spara din första inspiration-bild</p>
              <button onClick={() => setShowAdd(true)} className="neo-btn px-4 py-2 bg-[#FFC8DD] font-bold text-sm">
                + Lägg till bild
              </button>
            </div>
          ) : (
            <div className="columns-2 sm:columns-3 lg:columns-4 gap-2" data-testid="pin-grid">
              {pins.map((p) => (
                <PinCard key={p.id} pin={p} onDelete={remove} onClick={() => {}} onAddToProject={setPickerPinId} />
              ))}
            </div>
          )}
        </>
      )}

      <AddPinModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={load}
        rooms={rooms}
        defaultRoomId={filterRoom}
      />

      <AddToProjectPicker
        pinId={pickerPinId}
        open={!!pickerPinId}
        onClose={() => setPickerPinId(null)}
      />
    </div>
  );
}
